import { z } from "zod";
import { ConfigSchema, SiteDetailsSchema, type Config, type SiteDetails } from "@stackje/common/config";

export const SiteInsertPayloadSchema = z.object({
  subdomain: z
    .string()
    .min(4, { message: "Subdomain must be at least 4 characters long." })
    .max(63, { message: "Subdomain cannot exceed 63 characters." })
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
      message:
        "Subdomain can only contain lowercase letters, numbers, and hyphens. It cannot start or end with a hyphen or have consecutive hyphens.",
    }),
  config: ConfigSchema.optional(),
  siteDetails: SiteDetailsSchema.optional(),
});

// Schema for API requests (without userId since it comes from auth)
export const ApiSiteInsertPayloadSchema = z.object({
  subdomain: z
    .string()
    .min(4, { message: "Subdomain must be at least 4 characters long." })
    .max(63, { message: "Subdomain cannot exceed 63 characters." })
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
      message:
        "Subdomain can only contain lowercase letters, numbers, and hyphens. It cannot start or end with a hyphen or have consecutive hyphens.",
    }),
  config: ConfigSchema.optional()
    .transform((config) => (config ? JSON.stringify(config) : undefined)), // Keep undefined for optional
  siteDetails: SiteDetailsSchema.default({ // Keep default here for insert
    title: "",
    description:
      "Create, manage, and publish your website effortlessly—all from your phone.",
    colorSchemeId: "natural",
  }).transform((siteDetails) => JSON.stringify(siteDetails)), // Always stringify for insert
});

export const SiteUpdatePayloadSchema = z.object({
  config: z.union([
    ConfigSchema.transform((config) => JSON.stringify(config)),
    z.string(), // Already stringified JSON
    z.undefined()
  ]).optional(),
  siteDetails: z.union([
    SiteDetailsSchema.transform((siteDetails) => JSON.stringify(siteDetails)),
    z.string(), // Already stringified JSON
    z.undefined()
  ]).optional(),
});

export type Site = {
  id: string;
  subdomain: string;
  userId: string;
  config: Config | null;
  siteDetails: SiteDetails;
  customDomain: string | null;
  customDomainValidation: string | null;
  cloudflareHostnameId: string | null;
  lastModifiedAt: string | null;
  lastPublishedAt: string | null;
};

// Custom Domain schemas
export const CustomDomainRequestSchema = z.object({
  domain: z.string().min(1, "Domain is required"),
});

export const CustomDomainStatusSchema = z.object({
  status: z.enum(["pending_validation", "pending_deployment", "pending_deletion", "active", "moved", "deleted"]),
  sslStatus: z.string().optional(),
  verificationErrors: z.array(z.string()).optional(),
});

export type SiteInsertPayload = z.infer<typeof SiteInsertPayloadSchema>;
export type ApiSiteInsertPayload = z.infer<typeof ApiSiteInsertPayloadSchema>;
export type SiteUpdatePayload = z.infer<typeof SiteUpdatePayloadSchema>;
export type CustomDomainRequest = z.infer<typeof CustomDomainRequestSchema>;
export type CustomDomainStatus = z.infer<typeof CustomDomainStatusSchema>; 